import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AccountsInfo } from '../../../common/dtos/accounts-info.dto';
import { ClientSegmentContentDTO, ClientSegmentRedirectInstruction } from '../../../common/dtos/client-segment.dto';

@Injectable({
    providedIn: 'root',
})
export class AccountPermissionsService {
    private readonly httpClient = inject(HttpClient);


    /**
     * Creates HTTP headers with the current client URL for security tracking.
     * 
     * @returns HttpHeaders containing X-Client-URL header
     * 
     * @private
     * @remarks
     * The X-Client-URL header is used for security monitoring and helps track
     * which application URLs are making API requests.
     */
    private getHeaders(): HttpHeaders {
        return new HttpHeaders({
            'X-Client-URL': window.location.href,
        });
    }

    /**
     * Retrieves account and user information for the authenticated grantor.
     * 
     * @returns Observable<AccountsInfo> - Contains grantor info, owned accounts, and existing grantees
     * 
     * @remarks
     * This is the primary API call to load all account and permission data for the
     * authenticated user. It returns:
     * - Grantor's personal information
     * - List of accounts they own
     * - Existing grantees with access to those accounts
     * - Account types and eligibility information
     */
    getAccountUsers(): Observable<AccountsInfo> {
        return this.httpClient.get<AccountsInfo>(
            `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.GET_ACCOUNT_USERS}`,
        );
    }

    /**
     * Retrieves client segment-specific content or redirect instructions.
     * 
     * @returns Observable<ClientSegmentContentDTO | ClientSegmentRedirectInstruction> - Content or redirect based on client segment
     * 
     * @remarks
     * This API determines the appropriate content or redirect based on the user's
     * client segment (e.g., Individual, Institutional, Personal Investor).
     * Used to customize the user experience based on their account type.
     * Includes the current URL in headers for security tracking.
     */
    getClientSegmentContent(): Observable<ClientSegmentContentDTO | ClientSegmentRedirectInstruction> {
        return this.httpClient.get<ClientSegmentContentDTO>(
            `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.GET_CLIENT_SEGMENT_TYPE}`,
            { headers: this.getHeaders() }
        );
    }
}
